import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import type { TaskCardViewMode, KanbanOrientation } from '../components/tasks/TaskCard';

const DEFAULT_TASK_VIEW_MODE_KEY = 'flank-task-card-view-mode';
const DEFAULT_KANBAN_ORIENTATION_KEY = 'flank-kanban-orientation';

interface TaskViewProviderProps {
  children: ReactNode;
  /** Override localStorage key for list/kanban preference (e.g. Todos workspace). */
  viewModeStorageKey?: string;
  /** Override localStorage key for Kanban orientation preference. */
  orientationStorageKey?: string;
}

interface TaskViewContextValue {
  taskViewMode: TaskCardViewMode;
  setTaskViewMode: (mode: TaskCardViewMode) => void;
  kanbanOrientation: KanbanOrientation;
  setKanbanOrientation: (o: KanbanOrientation) => void;
}

const TaskViewContext = createContext<TaskViewContextValue | null>(null);

export function TaskViewProvider({
  children,
  viewModeStorageKey = DEFAULT_TASK_VIEW_MODE_KEY,
  orientationStorageKey = DEFAULT_KANBAN_ORIENTATION_KEY,
}: TaskViewProviderProps) {
  const [taskViewMode, setTaskViewModeState] = useState<TaskCardViewMode>(() => {
    if (typeof window === 'undefined') return 'list';
    const raw = window.localStorage.getItem(viewModeStorageKey);
    if (raw === 'kanban' || raw === 'bullets' || raw === 'list') return raw;
    return 'list';
  });

  const [kanbanOrientation, setKanbanOrientationState] = useState<KanbanOrientation>(() => {
    if (typeof window === 'undefined') return 'vertical';
    return window.localStorage.getItem(orientationStorageKey) === 'horizontal' ? 'horizontal' : 'vertical';
  });

  function setTaskViewMode(mode: TaskCardViewMode) {
    setTaskViewModeState(mode);
    if (typeof window !== 'undefined') window.localStorage.setItem(viewModeStorageKey, mode);
  }

  function setKanbanOrientation(o: KanbanOrientation) {
    setKanbanOrientationState(o);
    if (typeof window !== 'undefined') window.localStorage.setItem(orientationStorageKey, o);
  }

  return (
    <TaskViewContext.Provider value={{ taskViewMode, setTaskViewMode, kanbanOrientation, setKanbanOrientation }}>
      {children}
    </TaskViewContext.Provider>
  );
}

export function useTaskView() {
  const ctx = useContext(TaskViewContext);
  if (!ctx) throw new Error('useTaskView must be used within TaskViewProvider');
  return ctx;
}
